import React from "react";

type UploadDropzoneProps = {
  onFilesSelected: (files: FileList | File[]) => void;
  multiple?: boolean;
  accept?: string;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
  children?: React.ReactNode;
};

export const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  onFilesSelected,
  multiple = false,
  accept,
  className = "",
  disabled = false,
  ariaLabel = "Drop files here",
  children,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = React.useState(false);

  const handleClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    onFilesSelected(e.target.files);
    // allow picking same file again
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFilesSelected(files);
    }
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragOver(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={`${className} ${dragOver ? "drag-over" : ""}`}
      onClick={handleClick}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-disabled={disabled}
    >
      {children}
      <input
        ref={inputRef}
        type="file"
        className="d-none"
        accept={accept}
        multiple={multiple}
        onChange={onChangeInput}
        disabled={disabled}
      />
    </div>
  );
};



